import { useState, useEffect } from "react";
import { FaCheck } from "react-icons/fa";
import LabelInput from "../labelInput";
import Button from "../Button";
import {
  fetchModifications,
  fetchSecurity,
  fetchVehicleBodyType,
  fetchVehicleRegData,
} from "../../services/simpleListItemsService";
import VehicleLookupQuestions from "./VehicleLookupQuestions";
import ManualVehicleLookup from "./ManualVehicleLookup";
import FieldStack from "../FieldStack";

const VehicleLookup = ({
  STATE,
  formMethods,
  errorVR,
  setErrorVR,
  prevVeh = false,
  showLookupQuestions = true,
  showRegistrationUnknownToggle = true,
  registrationValidation,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    control,
    trigger,
    unregister,
  } = formMethods;

  const [modifications, setModifications] = useState([]);
  const [vehicleBodyTypes, setVehicleBodyTypes] = useState([]);
  const [security, setSecurity] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropDownLoading, setDropDownLoading] = useState(false);
  const [isRegistrationUnknown, setIsRegistrationUnknown] = useState(false);
  const [isPrevManualVehicleEntry, setIsPrevManualVehicleEntry] = useState(
    false,
  );
  const [isPrevVehicleConfirmation, setIsPrevVehicleConfirmation] = useState(
    false,
  );
  const [isPrevRegistrationLocked, setIsPrevRegistrationLocked] = useState(
    false,
  );
  const [internalErrorVR, setInternalErrorVR] = useState(null);

  const vehicleRegField = prevVeh ? "prevVehicleRegis" : "VehicleReg";
  const vehicleLookupField = prevVeh
    ? "prevLookupVehicleReg"
    : "lookupvehicleReg";
  const manualVehicleField = prevVeh
    ? "prevManualVehicleReg"
    : "manualVehicleReg";
  const vehicleError = errorVR ?? internalErrorVR;
  const setVehicleError = setErrorVR ?? setInternalErrorVR;
  const VehicleReg = watch(vehicleRegField);
  const isRegistrationLocked = isPrevRegistrationLocked;
  const isManualVehicleEntry = prevVeh
    ? isPrevManualVehicleEntry
    : STATE.isManualVehicleEntry;
  const setManualVehicleEntry = (value) => {
    if (prevVeh) {
      setIsPrevManualVehicleEntry(value);
      return;
    }

    STATE.setIsManualVehicleEntry(value);
  };
  const vehicleConfirmation = prevVeh
    ? isPrevVehicleConfirmation
    : STATE.vehicleConfirmation;
  const setVehicleConfirmation = (value) => {
    if (prevVeh) {
      setIsPrevVehicleConfirmation(value);
      return;
    }

    STATE.setVehicleConfirmation(value);
  };
  const setVehicleConfirmationBox = (value) => {
    if (!prevVeh) {
      STATE.setVehicleConfirmationBox(value);
    }
  };
  const setShowBasicLookupQuestions = (value) => {
    if (!prevVeh) {
      STATE.setShowBasicLookupQuestions(value);
    }
  };
  const setShowExtraLookupQuestions = (value) => {
    if (!prevVeh) {
      STATE.setShowExtraLookupQuestions(value);
    }
  };

  const setSupportVehicleFields = (vehicle) => {
    if (!prevVeh) {
      return;
    }

    setValue("prevVehicleAbiCode", vehicle?.abiCode ?? null);
    setValue("prevVehicleYear", vehicle?.year ?? null);
    setValue("prevVehicleMake", vehicle?.abiMake ?? null);
    setValue("prevVehicleModel", vehicle?.abiModel ?? null);
    setValue("prevVehicleRegDate", vehicle?.dateFirstRegistered ?? null);
    setValue(
      "prevVehImported",
      vehicle
        ? vehicle.imported === "true" || vehicle.imported === true
          ? "Yes"
          : "No"
        : null,
    );
    setValue("prevSeats", vehicle?.seats ?? null);
    setValue("prevLookupData", Boolean(vehicle));
    setValue("prevRightHandDrive", vehicle ? "Yes" : null);
    setValue("prevVehicleModified", vehicle ? "No" : null);
    setValue("prevVehicleQPlated", vehicle ? "No" : null);
  };

  useEffect(() => {
    const fetchData = async () => {
      setDropDownLoading(true);
      try {
        const bodyTypes = await fetchVehicleBodyType();
        const mods = await fetchModifications();
        const secs = await fetchSecurity();
        setVehicleBodyTypes(bodyTypes);
        setModifications(mods);
        setSecurity(secs);
      } catch (error) {
        console.error(
          "Error fetching modifications, security, and body types:",
          error,
        );
      } finally {
        setDropDownLoading(false);
      }
    };
    fetchData();
  }, []);

  const resetLookupData = () => {
    setVehicleConfirmation(false);
    setManualVehicleEntry(false);
    setShowBasicLookupQuestions(false);
    setShowExtraLookupQuestions(false);
    setVehicleConfirmationBox(false);
    setValue("abiCode", null);
    setValue("make", null);
    setValue("model", null);
    setValue("noOfSeats", null);
    setValue("cubicCapacity", null);
    setValue("yearOfManufacture", null);
    setValue("bodyTypeId", null);
    setValue("fuelTypeId", null);
    setValue("gearboxId", null);
    setValue("preVehicleSearch", null);
    setValue("vehicleQPlated", null);
    setValue("rightHandDrive", null);
    setValue("vehicleModified", null);
    setValue("modifications", null);
    setValue("vehImported", null);
    setValue("seats", null);
    setValue("dashCam", null);
    setValue("security", null);
    setValue("securityDevice", null);
    setValue("vehiclePurchased", null);
    setValue("manualMake", null);
    setValue("manualModel", null);
    setValue("manualYear", null);
    setValue("manualDoor", null);
    setValue("manualBodyTypeId", null);
    STATE.setManualBodyTypeId(null);
    setValue(vehicleLookupField, null);
    setValue(manualVehicleField, null);
    setSupportVehicleFields(null);
  };

  const handleLookupRegistration = async () => {
    if (isRegistrationLocked) {
      resetLookupData();
      setVehicleError("");
      setIsPrevRegistrationLocked(false);
      setIsRegistrationUnknown(false);
      setValue(vehicleRegField, null, { shouldDirty: true });
      return;
    }

    setVehicleError("");
    setIsRegistrationUnknown(false);
    setManualVehicleEntry(false);

    const isValid = await trigger(vehicleRegField);
    if (!isValid) {
      return;
    }

    setIsLoading(true);
    resetLookupData();
    try {
      let data = await fetchVehicleRegData(VehicleReg);
      if (data.isVehicleFound && data.vehicleFound) {
        setValue("abiCode", data.vehicleFound.abiCode);
        setValue("make", data.vehicleFound.abiMake);
        setValue("model", data.vehicleFound.abiModel);
        setValue("noOfSeats", parseInt(data.vehicleFound.seats));
        setValue("cubicCapacity", data.vehicleFound.abiCC);
        setValue("yearOfManufacture", data.vehicleFound.year);
        setValue("bodyTypeId", data.vehicleFound.bodyTypeId);
        setValue("fuelTypeId", data.vehicleFound.abiFuel);
        setValue("gearboxId", data.vehicleFound.abiShift);
        setValue("registeredDate", data.vehicleFound.dateFirstRegistered);
        setValue("preVehicleSearch", data.vehicleFound.abiVehicleInfoDesc);
        setValue("vehicleQPlated", "No");
        setValue(
          "vehImported",
          data.vehicleFound.imported === "true" ? "Yes" : "No",
        );
        setValue("rightHandDrive", "Yes");
        setValue("vehicleModified", "No");
        setValue("seats", data.vehicleFound.seats);
        setValue(vehicleLookupField, data.vehicleFound);
        setValue(manualVehicleField, null);
        setSupportVehicleFields(data.vehicleFound);
        setVehicleConfirmation(true);
        setIsPrevRegistrationLocked(true);
      } else {
        setValue("make", data.vehicleFound?.abiMake);
        setValue("model", data.vehicleFound?.abiModel);
        setValue(vehicleLookupField, null);
        setSupportVehicleFields(null);
        setManualVehicleEntry(true);
        setIsPrevRegistrationLocked(true);
      }
    } catch (error) {
      setVehicleError(
        prevVeh
          ? "An error occurred while fetching previous vehicle registration data."
          : "An error occurred while fetching vehicle registration data.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistrationUnknownToggle = () => {
    setVehicleError("");
    resetLookupData();
    setValue(vehicleRegField, null);

    if (isRegistrationUnknown) {
      setIsRegistrationUnknown(false);
      setIsPrevRegistrationLocked(false);
      return;
    }

    unregister(vehicleRegField);
    setIsRegistrationUnknown(true);
    setManualVehicleEntry(true);
  };

  const handleYesClick = () => {
    setVehicleError("");
    setVehicleConfirmationBox(false);
    setShowBasicLookupQuestions(false);
    setShowExtraLookupQuestions(true);
  };

  const handleEditClick = () => {
    setVehicleError("");
    setVehicleConfirmationBox(false);
    setShowBasicLookupQuestions(true);
    setShowExtraLookupQuestions(true);
  };

  const handleManualVehicleSelect = (index, item, meta) => {
    setValue(vehicleLookupField, null);
    setValue(manualVehicleField, item);

    if (prevVeh) {
      setSupportVehicleFields(item);
      setValue("prevVehicleDoor", meta?.manualDoor ?? null);
    }
  };

  return (
    <FieldStack className="tw-w-full tw-max-w-full">
      {!isRegistrationUnknown && (
        <div className="tw-w-full">
          <label
            className="tw-mb-2 tw-block tw-text-base tw-font-bold tw-text-dis-navy"
            htmlFor={vehicleRegField}
          >
            Vehicle registration
          </label>
          <div className="tw-flex tw-w-full tw-items-start tw-gap-2.5 max-[640px]:tw-flex-wrap">
            <LabelInput
              id={vehicleRegField}
              type="text"
              placeholder="e.g EA21 WLP"
              register={register(vehicleRegField, {
                required: "Required",
                ...registrationValidation,
              })}
              errors={errors[vehicleRegField]}
              watch={watch(vehicleRegField)}
              trigger={trigger}
              autoComplete="off"
              disabled={isLoading}
              readOnly={isRegistrationLocked}
            />
            <Button
              type="button"
              size="card"
              loading={isLoading}
              loadingText="Finding..."
              className="tw-flex-none max-[640px]:tw-w-full"
              onClick={handleLookupRegistration}
            >
              {isRegistrationLocked ? "Change registration" : "Find my vehicle"}
            </Button>
          </div>
        </div>
      )}

      {showRegistrationUnknownToggle && !isRegistrationLocked && (
        <Button
          type="button"
          variant="soft"
          size="small"
          onClick={handleRegistrationUnknownToggle}
          disabled={isLoading}
        >
          {isRegistrationUnknown
            ? "I know the registration"
            : "I don't know the registration"}
        </Button>
      )}

      {isManualVehicleEntry ? (
        <ManualVehicleLookup
          register={register}
          watch={watch}
          control={control}
          trigger={trigger}
          errors={errors}
          STATE={STATE}
          setValue={setValue}
          setErrorVR={setVehicleError}
          resetLookupData={resetLookupData}
          isRegistrationUnknown={isRegistrationUnknown}
          onVehicleSelect={handleManualVehicleSelect}
          setManualVehicleEntry={setManualVehicleEntry}
          showLookupQuestions={showLookupQuestions}
        />
      ) : (
        <>
          {watch("make") && (
            <FieldStack
              className="tw-w-full"
              gapClassName={
                STATE.vehicleConfirmationBox ? "tw-gap-[18px]" : "tw-gap-0"
              }
            >
              <div
                className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-dis-border tw-bg-dis-info-panel tw-py-4 tw-pl-4 tw-pr-2.5"
                role="status"
                aria-label="Vehicle details"
              >
                <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-2">
                  <p className="tw-m-0 tw-text-base tw-font-bold tw-text-dis-navy">
                    Vehicle details
                  </p>
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    <p className="tw-m-0 tw-break-words tw-text-sm tw-leading-[1.5] tw-text-dis-text-2">
                      {watch("make")}
                    </p>
                    <p className="tw-m-0 tw-break-words tw-text-sm tw-leading-[1.5] tw-text-dis-text-2">
                      {watch("model")}
                    </p>
                    {watch("preVehicleSearch") && (
                      <p className="tw-m-0 tw-break-words tw-text-sm tw-leading-[1.5] tw-text-dis-text-2">
                        {watch("preVehicleSearch")}
                      </p>
                    )}
                  </div>
                </div>
                <span
                  className="tw-flex tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-dis-success tw-text-white"
                  aria-hidden="true"
                >
                  <FaCheck className="tw-h-2.5 tw-w-2.5" />
                </span>
              </div>
              {vehicleConfirmation && (
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-2.5 tw-border-t tw-border-dis-divider tw-pt-3">
                  <p className="tw-m-0 tw-text-base tw-font-bold tw-text-dis-navy">
                    Is this your vehicle?
                  </p>
                  <div className="tw-flex tw-justify-start tw-gap-2.5">
                    <button
                      type="button"
                      className="tw-flex tw-h-11 tw-w-max tw-cursor-pointer tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-full tw-border-2 tw-border-dis-sky tw-bg-dis-sky tw-px-5 tw-text-sm tw-font-bold tw-text-dis-navy hover:tw-border-dis-sky-hover hover:tw-bg-dis-sky-hover"
                      onClick={() => {
                        setVehicleConfirmation(false);
                        setVehicleConfirmationBox(true);
                      }}
                    >
                      Yes
                    </button>

                    <button
                      type="button"
                      className="tw-flex tw-h-11 tw-w-max tw-cursor-pointer tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-full tw-border-2 tw-border-dis-field tw-bg-dis-field tw-px-5 tw-text-sm tw-font-bold tw-text-dis-navy hover:tw-border-dis-field-hover hover:tw-bg-dis-field-hover"
                      onClick={() => {
                        resetLookupData();
                        setVehicleError("Please try a different registration");
                      }}
                    >
                      No
                    </button>
                  </div>
                </div>
              )}

              {showLookupQuestions && (
                <VehicleLookupQuestions
                  STATE={STATE}
                  register={register}
                  trigger={trigger}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  control={control}
                  isLoading={isLoading}
                  dropDownLoading={dropDownLoading}
                  modifications={modifications}
                  vehicleBodyTypes={vehicleBodyTypes}
                  security={security}
                  handleYesClick={handleYesClick}
                  handleEditClick={handleEditClick}
                />
              )}
            </FieldStack>
          )}
        </>
      )}
      <span className="tw-block tw-text-sm tw-text-dis-error">
        {vehicleError}
      </span>
    </FieldStack>
  );
};

export default VehicleLookup;
